import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import MarketingLayout from "../../marketing/components/MarketingLayout";
import EmailVerificationModal from "../../components/Modal/EmailVerificationModal";

const SignUp = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    company_name: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setError("Vous devez accepter les conditions générales d'utilisation.");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await signup(form);
      setShowVerificationModal(true);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.detail ||
          "Échec de l'inscription. Veuillez vérifier les informations fournies."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MarketingLayout showFinalCta={false} variant="auth">
      <Helmet>
        <title>Créer un compte - ASAP Hub</title>
        <meta name="description" content="Créez votre compte ASAP Hub et démarrez votre essai gratuit. Plateforme IA pour ESN et cabinets de conseil." />
      </Helmet>

      <section className="mk-contact-section" data-testid="signup-page">
        <div className="mk-wrap mk-contact-grid">
          <aside className="mk-reveal">
            <span className="mk-eyebrow">
              <span className="mk-pulse" aria-hidden="true" />
              Démarrer avec ASAP Hub
            </span>
            <h1>Créez votre compte</h1>
            <p className="mk-lead">
              Rejoignez les ESN et cabinets qui pilotent leur activité à la vitesse de l'IA. Configuration en quelques minutes, sans engagement.
            </p>
            <div className="mk-info-list">
              <div className="mk-info-item">
                <i aria-hidden="true">✦</i>
                <div><b>Essai gratuit</b>Découvrez la plateforme sans carte bancaire</div>
              </div>
              <div className="mk-info-item">
                <i aria-hidden="true">⚡</i>
                <div><b>Modulaire</b>Activez Profile, Partners, Skills, Pilot ou CV à la demande</div>
              </div>
              <div className="mk-info-item">
                <i aria-hidden="true">✓</i>
                <div><b>100% RGPD</b>Hébergé en France, conforme au RGPD</div>
              </div>
            </div>
          </aside>

          <div className="mk-contact-card mk-reveal">
            <form onSubmit={handleSubmit} noValidate>
              <h3>Créer un compte</h3>

              {error && (
                <div className="mk-form-error" role="alert" data-testid="signup-error">{error}</div>
              )}

              <div className="mk-field">
                <label htmlFor="company_name">Nom de l'entreprise <span className="mk-required">*</span></label>
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  value={form.company_name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  autoComplete="organization"
                  required
                />
              </div>

              <div className="mk-form-row">
                <div className="mk-field">
                  <label htmlFor="first_name">Prénom <span className="mk-required">*</span></label>
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={form.first_name}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="mk-field">
                  <label htmlFor="last_name">Nom <span className="mk-required">*</span></label>
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={form.last_name}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <div className="mk-field">
                <label htmlFor="email">Email professionnel <span className="mk-required">*</span></label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="mk-field">
                <label htmlFor="password">Mot de passe <span className="mk-required">*</span></label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                />
              </div>
              <p className="mk-auth-hint">
                Minimum 10 caractères avec au moins une majuscule, une minuscule, un nombre et un caractère spécial.
              </p>

              <div style={{ marginTop: 16 }}>
                <label className="mk-auth-checkbox">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={isSubmitting}
                    required
                  />
                  <span>
                    J'ai lu et j'accepte les{" "}
                    <Link to="/resource/MentionsLegales" target="_blank" rel="noopener noreferrer">conditions générales d'utilisation</Link>{" "}
                    et la{" "}
                    <Link to="/resource/Politique" target="_blank" rel="noopener noreferrer">politique de confidentialité</Link>.
                  </span>
                </label>

              </div>

              <button
                type="submit"
                className="mk-btn mk-btn-primary"
                disabled={isSubmitting}
                style={{ width: "100%", padding: "14px 18px", marginTop: 12 }}
                data-testid="signup-submit"
              >
                {isSubmitting ? "Création en cours…" : "Créer mon compte"}
              </button>

              <p className="mk-auth-help">
                Vous avez déjà un compte ? <Link to="/login">Se connecter</Link>
              </p>
            </form>
          </div>
        </div>
      </section>

      <EmailVerificationModal
        show={showVerificationModal}
        onClose={() => {
          setShowVerificationModal(false);
          navigate("/login");
        }}
        email={form.email}
      />
    </MarketingLayout>
  );
};

export default SignUp;
